import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, FileText, Flame, Vote, Coins, ShieldCheck, Map,
  ExternalLink, Copy, Leaf, TreePine, Cog, Droplets, HeartHandshake,
} from "lucide-react";
import { toast } from "sonner";

const CONTRACT_ADDRESS = "0x246502B687a23fD4b37eD362263f6a52aF5C01C5";
const CHARITY_WALLET = "0xA7daEfe1522b3868874F79db32290b5bdd2EC2a2";
const OPERATIONS_WALLET = "0x43D1Fe8BBC3113ccD43094bdF5a044C8BcFA5bBa";
const LIQUIDITY_WALLET = "0x8bbfFCE68baBF5881C7B9BA17B68FD3c40a7a89B";
const scan = (a) => `https://basescan.org/address/${a}`;

const chapters = [
  { id: "ch1", num: "01", title: "Executive Summary & The Wildfire Crisis", icon: Flame },
  { id: "ch2", num: "02", title: "The $AFTBRN On-Chain Solution & Governance", icon: Vote },
  { id: "ch3", num: "03", title: "Tokenomics", icon: Coins },
  { id: "ch4", num: "04", title: "Radical Transparency", icon: ShieldCheck },
  { id: "ch5", num: "05", title: "Project Roadmap", icon: Map },
];

const Callout = ({ children }) => (
  <div className="my-6 border-l-2 border-ember bg-ember/5 pl-5 pr-4 py-4 text-muted-foreground">
    {children}
  </div>
);

const H3 = ({ children }) => (
  <h3 className="font-display font-bold text-xl sm:text-2xl mt-10 mb-4 text-foreground">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-muted-foreground leading-relaxed mb-5 text-[15px] sm:text-base">{children}</p>
);

const Chapter = ({ ch, children }) => (
  <section id={ch.id} data-testid={`chapter-${ch.id}`} className="scroll-mt-28 mb-20">
    <div className="flex items-center gap-3 mb-2">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Chapter {ch.num}</span>
    </div>
    <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-6 flex items-start gap-3">
      <ch.icon className="w-7 h-7 text-ember mt-1 shrink-0" />
      <span>{ch.title}</span>
    </h2>
    {children}
  </section>
);

export default function Whitepaper() {
  const [active, setActive] = useState("ch1");
  const observerRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    chapters.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) obs.observe(el);
    });
    observerRef.current = obs;
    return () => obs.disconnect();
  }, []);

  const copyAddr = async (a) => {
    try {
      await navigator.clipboard.writeText(a);
      toast.success("Address copied!");
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  return (
    <main className="bg-background text-foreground min-h-screen" data-testid="whitepaper-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="wp-back-home" className="flex items-center gap-2.5 group">
            <img src="/logo-icon.png" alt="AfterBurn" className="w-8 h-8 object-contain" />
            <span className="font-display font-bold text-lg">
              After<span className="text-ember">Burn</span>
            </span>
            <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border border-white/15 px-2 py-0.5">
              Docs
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wide text-muted-foreground hover:text-ember transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 lg:grid lg:grid-cols-12 lg:gap-12">
        {/* Sidebar / chapter nav */}
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <p className="hidden lg:block font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Contents
            </p>
            <nav
              data-testid="wp-sidebar"
              className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 sticky top-16 lg:static bg-background/95 lg:bg-transparent z-30"
            >
              {chapters.map((c) => (
                <a
                  key={c.id}
                  href={`#${c.id}`}
                  data-testid={`wp-nav-${c.id}`}
                  className={`shrink-0 lg:shrink flex items-center gap-2.5 border px-3 py-2.5 text-sm transition-colors ${
                    active === c.id
                      ? "border-ember/60 bg-ember/10 text-foreground"
                      : "border-white/10 text-muted-foreground hover:border-white/25 hover:text-foreground"
                  }`}
                >
                  <span className={`font-mono text-xs ${active === c.id ? "text-ember" : ""}`}>{c.num}</span>
                  <span className="hidden lg:inline leading-tight">{c.title}</span>
                  <span className="lg:hidden whitespace-nowrap">{c.title.split(" ")[0]}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="lg:col-span-9 mt-10 lg:mt-0 max-w-3xl">
          {/* Cover */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16 border border-white/10 bg-card p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="absolute -right-8 -top-8 opacity-10">
              <FileText className="w-40 h-40 text-ember" />
            </div>
            <div className="relative">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Whitepaper · v1.0</span>
              <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight">
                AfterBurn <span className="text-ember">($AFTBRN)</span>
              </h1>
              <p className="mt-4 text-lg text-muted-foreground">
                A Regenerative Finance (ReFi) protocol on Base, channeling on-chain liquidity into
                verifiable wildfire recovery — reforestation and community reconstruction.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 font-mono text-xs">
                <span className="border border-white/15 px-3 py-1.5 text-muted-foreground">Network: Base L2</span>
                <span className="border border-white/15 px-3 py-1.5 text-muted-foreground">Standard: ERC-20</span>
                <span className="border border-white/15 px-3 py-1.5 text-muted-foreground">Category: ReFi / Impact</span>
              </div>
            </div>
          </motion.div>

          {/* A Note from the Founder */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 border border-white/10 bg-card p-8 sm:p-10"
            data-testid="wp-founder-note"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 shrink-0 rounded-full bg-gradient-to-br from-ember/25 to-emerald-500/25 border border-white/10 flex items-center justify-center">
                <Leaf className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">A Note from the Founder</span>
                <p className="font-display font-bold text-xl mt-1">Barry McColman</p>
                <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">
                  Nipissing First Nation · NFN Reserve, Ontario, Canada
                </p>
              </div>
            </div>
            <P>
              Welcome. I'm Barry McColman - Bear, founder of AfterBurn and a member of Nipissing First
              Nation. I live on the NFN Reserve in Ontario, Canada, where I hunt and fish for sustenance —
              a way of life that depends entirely on healthy forests.
            </P>
            <P>
              This project isn't abstract to me. Protecting the boreal is home and survival. AfterBurn
              exists to fund the three things it needs to recover: reforestation, rebuilding the communities
              that lose everything, and supporting the first responders on the front lines — not just for me
              now, but for the generations that come after us.
            </P>
          </motion.div>

          {/* Chapter 1 */}
          <Chapter ch={chapters[0]}>
            <P>
              AfterBurn ($AFTBRN) is a Regenerative Finance protocol engineered to convert the velocity
              of decentralized markets into durable, real-world ecological and social restoration. By
              embedding a transparent fiscal mechanism directly into an ERC-20 token deployed on the Base
              network, AfterBurn creates a self-sustaining funding pipeline for post-wildfire recovery —
              financing native reforestation and the reconstruction of the communities left in the ashes.
            </P>
            <P>
              Where traditional philanthropy is episodic, opaque, and dependent on discretionary donor
              cycles, AfterBurn is continuous, auditable, and programmatic. Every transaction across the
              network contributes to a mission treasury whose flows are visible on a public ledger in real
              time. Capital formation and capital deployment are, for the first time, part of the same
              on-chain event.
            </P>

            <H3>The Wildfire Crisis</H3>
            <P>
              Wildfires have escalated from seasonal hazard to structural climate emergency. A warming
              climate, prolonged drought, and accumulated fuel loads have produced fire seasons of
              unprecedented severity, incinerating tens of millions of hectares of forest annually and
              displacing communities across five continents. The consequences compound: destroyed carbon
              sinks, collapsed watersheds, eroded soils, lost biodiversity, and human settlements reduced
              to rubble.
            </P>
            <P>
              Recovery, however, is chronically underfunded. Emergency response captures headlines and
              short-lived donations; the multi-year work of replanting native forests and rebuilding homes,
              schools, and infrastructure receives a fraction of the attention and a smaller fraction of the
              money. This is the funding gap AfterBurn exists to close.
            </P>
            <Callout>
              <strong className="text-foreground">The AfterBurn thesis:</strong> if even a sliver of on-chain
              trading volume can be permanently and transparently routed toward restoration, a global,
              always-on treasury for recovery becomes possible — governed by the very community that funds it.
            </Callout>
          </Chapter>

          {/* Chapter 2 */}
          <Chapter ch={chapters[1]}>
            <P>
              The AfterBurn protocol is defined by a smart contract on the Base Layer-2 network. Base offers
              the settlement assurances of Ethereum with negligible transaction costs, making micro-scale
              contributions economically viable and inviting broad, permissionless participation.
            </P>
            <H3>The On-Chain Mechanism</H3>
            <P>
              A protocol-level transaction tax is applied to every transfer of $AFTBRN and automatically
              partitioned across three dedicated, publicly-verifiable wallets — Charity, Operations, and
              Liquidity. No intermediary custodies these flows; the contract itself enforces the split. This
              transforms ordinary market activity into a perpetual funding engine for restoration, with zero
              reliance on manual disbursement or trusted third parties.
            </P>
            <H3>Governance & Community Voting</H3>
            <P>
              AfterBurn is designed to progressively decentralize into a community-governed treasury.
              $AFTBRN holders are the protocol's stakeholders and, ultimately, its decision-makers. Through
              a token-weighted governance framework, the community proposes and ratifies which reforestation
              partners, rebuilding projects, and regional recovery initiatives receive funding from the
              charity treasury.
            </P>
            <P>
              Governance is exercised through structured proposals — each specifying a beneficiary, a
              funding amount, measurable deliverables, and a verification methodology. Proposals proceed to
              a token-weighted vote; those that pass are executed against the treasury and tracked to
              completion on-chain. This aligns capital allocation with the collective conviction of the
              people who hold the asset.
            </P>
            <Callout>
              One token, one voice. Governance ensures that AfterBurn's impact is directed by its community
              rather than by any single founder, foundation, or gatekeeper.
            </Callout>
          </Chapter>

          {/* Chapter 3 */}
          <Chapter ch={chapters[2]}>
            <H3>Supply</H3>
            <P>
              $AFTBRN launches with a fixed maximum supply of <strong className="text-foreground">10,000,000,000
              (10 billion) tokens</strong> on the Base network. The supply is immutable; no additional tokens
              can be minted, providing holders with hard, predictable scarcity.
            </P>

            <H3>The 7% Transaction Tax</H3>
            <P>
              A 7% tax is levied on trades and automatically routed by the contract into three purpose-built
              wallets. This is the protocol's fiscal heart — a continuous, self-executing allocation that
              funds the mission, sustains operations, and deepens market liquidity with every transaction.
            </P>

            <div className="grid sm:grid-cols-3 gap-4 my-8">
              {[
                { pct: "3%", label: "Charity", desc: "Automated Wildfire Relief Fund", icon: HeartHandshake, color: "#34D399", addr: CHARITY_WALLET },
                { pct: "3%", label: "Operations", desc: "Core Project Operations & Maintenance", icon: Cog, color: "#FF5A1F", addr: OPERATIONS_WALLET },
                { pct: "1%", label: "Liquidity", desc: "Automated Chart Liquidity Injection", icon: Droplets, color: "#60A5FA", addr: LIQUIDITY_WALLET },
              ].map((t) => (
                <div key={t.label} className="border border-white/10 bg-card p-5" data-testid={`wp-tax-${t.label.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <t.icon className="w-5 h-5" style={{ color: t.color }} />
                    <span className="font-display font-bold text-2xl" style={{ color: t.color }}>{t.pct}</span>
                  </div>
                  <p className="mt-3 font-display font-bold">{t.label}</p>
                  <p className="text-sm text-muted-foreground leading-snug mt-1">{t.desc}</p>
                  <a
                    href={scan(t.addr)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-ember transition-colors"
                  >
                    {t.addr.slice(0, 6)}…{t.addr.slice(-4)} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>

            <P>
              <strong className="text-foreground">3% — Charity:</strong> the Automated Wildfire Relief Fund,
              the philanthropic core of the protocol. These funds finance native tree planting and community
              reconstruction as directed by governance.
            </P>
            <P>
              <strong className="text-foreground">3% — Operations:</strong> Core Project Operations &amp;
              Maintenance, covering development, audits, partnerships, marketing, and the human infrastructure
              required to scale the ecosystem responsibly.
            </P>
            <P>
              <strong className="text-foreground">1% — Liquidity:</strong> an Automated Chart Liquidity
              Injection that continuously reinforces the trading pool, reducing volatility and improving
              price stability for all participants.
            </P>

            <H3>Contract</H3>
            <div className="border border-white/10 bg-card p-4 flex items-center gap-3 flex-wrap" data-testid="wp-contract-row">
              <span className="font-mono text-xs text-muted-foreground shrink-0"><span className="text-ember">Contract:</span></span>
              <span className="font-mono text-xs truncate flex-1 min-w-0" title={CONTRACT_ADDRESS}>{CONTRACT_ADDRESS}</span>
              <button
                onClick={() => copyAddr(CONTRACT_ADDRESS)}
                data-testid="wp-copy-contract"
                className="shrink-0 inline-flex items-center gap-1 border border-white/15 px-2 py-1 text-muted-foreground hover:border-ember hover:text-ember transition-colors"
              >
                <Copy className="w-3.5 h-3.5" /> <span className="font-mono text-[10px] uppercase">Copy</span>
              </button>
            </div>
          </Chapter>

          {/* Chapter 4 */}
          <Chapter ch={chapters[3]}>
            <P>
              Trust in impact finance has historically been asymmetric: donors send capital and hope it is
              used as promised. AfterBurn inverts this relationship through radical, verifiable transparency.
              Every unit of value that flows through the protocol is observable, timestamped, and immutable.
            </P>
            <H3>On-Chain Auditability</H3>
            <P>
              The Charity, Operations, and Liquidity wallets are public addresses on the Base network. Any
              person, anywhere, can inspect their balances and complete transaction histories on the BaseScan
              block explorer — no permission, login, or intermediary required. The protocol's finances are,
              by construction, an open book.
            </P>
            <div className="flex flex-wrap gap-3 my-6">
              <a href={scan(CHARITY_WALLET)} target="_blank" rel="noopener noreferrer" data-testid="wp-audit-charity" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm hover:border-ember hover:text-ember transition-colors">
                <TreePine className="w-4 h-4 text-emerald-400" /> Charity Wallet <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a href={scan(OPERATIONS_WALLET)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm hover:border-ember hover:text-ember transition-colors">
                <Cog className="w-4 h-4 text-ember" /> Operations Wallet <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a href={scan(LIQUIDITY_WALLET)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm hover:border-ember hover:text-ember transition-colors">
                <Droplets className="w-4 h-4 text-sky-400" /> Liquidity Wallet <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <H3>From Ledger to Living Forest</H3>
            <P>
              On-chain transparency establishes where the money goes; AfterBurn extends that assurance into
              the physical world. Disbursements from the charity treasury are matched to specific,
              named restoration projects and paired with off-chain evidence: partner attestations,
              geo-tagged planting coordinates, satellite and drone imagery, tree counts, and survival-rate
              monitoring over time.
            </P>
            <P>
              Our long-term objective is a public Impact Dashboard that reconciles each on-chain disbursement
              with its real-world outcome — hectares restored, trees planted and verified alive, and homes
              rebuilt — creating a continuous, auditable chain of custody from a single trade all the way to a
              living forest.
            </P>
            <Callout>
              Transparency is not a feature of AfterBurn; it is the product. If it cannot be verified, it will
              not be claimed.
            </Callout>
          </Chapter>

          {/* Chapter 5 */}
          <Chapter ch={chapters[4]}>
            <P>
              AfterBurn's roadmap advances in four deliberate phases — from ignition to a fully
              community-governed restoration engine.
            </P>
            {[
              {
                phase: "Phase 1 — Spark",
                items: [
                  "Token concept, mission, and whitepaper finalized",
                  "$AFTBRN smart contract deployed on Base with the 7% tax architecture",
                  "Charity, Operations, and Liquidity wallets published for public audit",
                  "Community channels launched and website live",
                ],
              },
              {
                phase: "Phase 2 — Ignite",
                items: [
                  "Presale / launchpad campaign and initial liquidity provisioning",
                  "Third-party contract security audit and liquidity lock",
                  "First reforestation and rebuilding partners onboarded",
                  "Exchange visibility and market-tracking integrations",
                ],
              },
              {
                phase: "Phase 3 — Regrow",
                items: [
                  "Governance framework and token-weighted proposal voting go live",
                  "First community-ratified restoration grants disbursed on-chain",
                  "Launch of the public Impact Dashboard (on-chain flows ↔ real-world proof)",
                  "Milestone: first one million trees funded and verified",
                ],
              },
              {
                phase: "Phase 4 — Restore",
                items: [
                  "Progressive decentralization toward a community-run treasury (DAO)",
                  "Scaled community-reconstruction programs across affected regions",
                  "Global wildfire-response partner network and institutional collaborations",
                  "Continuous transparency reporting and independent impact verification",
                ],
              },
            ].map((p, i) => (
              <div key={p.phase} className="border border-white/10 bg-card p-6 mb-4" data-testid={`wp-phase-${i + 1}`}>
                <h3 className="font-display font-bold text-xl mb-3 flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-ember" /> {p.phase}
                </h3>
                <ul className="space-y-2">
                  {p.items.map((it) => (
                    <li key={it} className="text-muted-foreground flex items-start gap-2 text-[15px]">
                      <span className="w-1.5 h-1.5 bg-ember mt-2 shrink-0" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Chapter>

          {/* Disclaimer */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="font-mono text-xs text-muted-foreground leading-relaxed">
              Disclaimer: This whitepaper is provided for informational purposes only and does not constitute
              financial, investment, legal, or tax advice, nor an offer or solicitation to buy or sell any
              asset. $AFTBRN is a utility and community token; cryptocurrencies are volatile and carry risk.
              Conduct your own research and consult qualified professionals before participating. © {new Date().getFullYear()} AfterBurn · after-burn.org
            </p>
          </div>
        </article>
      </div>
    </main>
  );
}
